"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "@/providers/TranslationsProvider";

/** Round, quiet icon button for a file row's remove / cancel action. */
const ICON_BUTTON = "shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[var(--foreground-tertiary)] hover:text-[var(--foreground)] hover:bg-black/5 dark:hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0071E3]/20";

/*
 * Ported from the dashboard's dropzone (src/components/shared/UploadManager/FileUpload.tsx):
 * drag & drop or click, per-file upload state with cancel, then a list with
 * open/remove. Differences: values carry their name/type (they come back from
 * the server, so the name can't be recovered from a URL), there's no
 * FileViewer in rawej - files open in a new tab - and the look follows
 * apple.com instead of the dashboard theme.
 */

export interface UploadedFile {
  id: string;
  url: string;
  name: string;
  type?: string;
}
interface FileUploadProps {
  value: UploadedFile[];
  onUpload: (file: File, signal: AbortSignal) => Promise<UploadedFile>;
  onRemove: (file: UploadedFile) => Promise<void> | void;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  /** In bytes; checked before uploading. */
  maxSize?: number;
  label?: string;
  hint?: string;
  error?: string;
  className?: string;
}
interface UploadingFile {
  id: string;
  file: File;
  status: "uploading" | "error";
}
const DEFAULT_ACCEPT = "image/jpeg,image/png,image/gif,image/webp,application/pdf";
function isAccepted(file: File, accept: string): boolean {
  if (accept === "*") return true;
  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return accept.split(",").map(type => type.trim()).some(type => {
    if (type.startsWith(".")) return extension === type;
    if (type.endsWith("/*")) return file.type.startsWith(type.replace("/*", ""));
    return file.type === type;
  });
}
function FileIcon(t0) {
  const $ = _c(7);
  const {
    type
  } = t0;
  let t1;
  if ($[0] !== type) {
    t1 = type?.includes("pdf");
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isPdf = t1;
  const t2 = `shrink-0 w-10 h-10 rounded-[10px] flex items-center justify-center text-[10px] font-semibold ${isPdf ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "bg-[#0071E3]/10 text-[#0071E3]"}`;
  let t3;
  if ($[2] !== isPdf) {
    t3 = isPdf ? "PDF" : <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.6-4.6a2 2 0 012.8 0L16 16m-2-2l1.6-1.6a2 2 0 012.8 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
    $[2] = isPdf;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <span className={t2} aria-hidden={true}>{t3}</span>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function Spinner() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="w-5 h-5 animate-spin text-[#0071E3]" viewBox="0 0 24 24" aria-hidden={true}><circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" /><path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function FileUpload({
  value,
  onUpload,
  onRemove,
  accept = DEFAULT_ACCEPT,
  multiple = false,
  maxFiles,
  maxSize,
  label,
  hint,
  error,
  className = ""
}: Readonly<FileUploadProps>) {
  const {
    t
  } = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const controllers = useRef<Record<string, AbortController>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState("");
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);

  // Abort anything still in flight when the step unmounts.
  useEffect(() => {
    const active = controllers.current;
    return () => Object.values(active).forEach(c => c.abort());
  }, []);
  const slots = maxFiles ? Math.max(0, maxFiles - value.length - uploading.filter(u => u.status === "uploading").length) : Infinity;
  const full = slots === 0;
  const handleFiles = useCallback(async (list: FileList | File[]) => {
    const files = Array.from(list);
    if (!files.length) return;
    const valid = files.filter(file => isAccepted(file, accept) && (!maxSize || file.size <= maxSize));
    setLocalError(valid.length < files.length ? t("upload.invalid file", "Use a PDF, PNG or JPG file within the size limit.") : "");
    const batch = (multiple ? valid : valid.slice(0, 1)).slice(0, slots);
    await Promise.all(batch.map(async file_0 => {
      const id = Math.random().toString(36).slice(2);
      const controller = new AbortController();
      controllers.current[id] = controller;
      setUploading(prev => [...prev, {
        id,
        file: file_0,
        status: "uploading"
      }]);
      try {
        await onUpload(file_0, controller.signal);
        setUploading(prev_2 => prev_2.filter(u_2 => u_2.id !== id));
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          setUploading(prev_0 => prev_0.filter(u_0 => u_0.id !== id));
        } else {
          setUploading(prev_1 => prev_1.map(u_1 => u_1.id === id ? {
            ...u_1,
            status: "error"
          } : u_1));
        }
      } finally {
        delete controllers.current[id];
      }
    }));
  }, [accept, maxSize, multiple, onUpload, slots, t]);
  const cancel = (id_0: string) => {
    controllers.current[id_0]?.abort();
    setUploading(prev_3 => prev_3.filter(u_3 => u_3.id !== id_0));
  };
  const remove = async (file_1: UploadedFile) => {
    setRemoving(file_1.id);
    try {
      await onRemove(file_1);
    } finally {
      setRemoving(null);
    }
  };
  const shownError = error || localError;
  return <div className={className}>
      {label && <p className="mb-2 text-[13px] font-medium text-[var(--foreground-secondary)]">{label}</p>}

      {!full && <button type="button" onClick={() => inputRef.current?.click()} onDrop={e => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    }} onDragOver={e_0 => {
      e_0.preventDefault();
      setIsDragging(true);
    }} onDragLeave={e_1 => {
      e_1.preventDefault();
      setIsDragging(false);
    }} className={`w-full flex items-center gap-4 rounded-[22px] border-[1.5px] border-dashed px-5 py-3.5 text-start transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0071E3]/20 ${isDragging ? "border-[#0071E3] bg-[#0071E3]/[0.06] scale-[1.01]" : shownError ? "border-[#E30000] bg-[#FFF2F4] dark:bg-[#3A1D1D]" : "border-[#C7C7CC] dark:border-[#48484A] bg-[#F2F2F7] dark:bg-[#2C2C2E] hover:border-[#0071E3] hover:bg-[#0071E3]/[0.05]"}`}>
          <span className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDragging ? "bg-[#0071E3] text-white" : "bg-white dark:bg-[#1C1C1E] text-[#0071E3] shadow-sm"}`} aria-hidden>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
            </svg>
          </span>
          <span className="min-w-0">
            <span className="block text-[15px] leading-5 text-[var(--foreground)]">
              {isDragging ? t("upload.drop", "Drop to upload") : t("upload.drag drop", "Drag & drop files here")}
            </span>
            <span className="block text-[13px] leading-5 text-[var(--foreground-tertiary)]">
              <span className="text-[#0066CC] dark:text-[#2997FF]">{t("upload.browse", "or click to browse")}</span>
              {hint && <> · {hint}</>}
            </span>
          </span>
        </button>}

      <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="hidden" data-testid="file-upload-input" onChange={e_2 => {
      if (e_2.target.files) handleFiles(e_2.target.files);
      e_2.target.value = "";
    }} />

      {(uploading.length > 0 || value.length > 0) && <ul className="mt-3 divide-y divide-[#D2D2D7]/60 dark:divide-[#48484A]/60 rounded-[26px] bg-[#F2F2F7] dark:bg-[#2C2C2E] overflow-hidden">
          {uploading.map(u_4 => <li key={u_4.id} className="flex items-center gap-3 px-4 py-3">
              {u_4.status === "uploading" ? <Spinner /> : <FileIcon type={u_4.file.type} />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] text-[var(--foreground)]">{u_4.file.name}</span>
                <span className={`block text-[12px] ${u_4.status === "error" ? "text-[#E30000]" : "text-[var(--foreground-tertiary)]"}`}>
                  {u_4.status === "error" ? t("upload.failed", "Upload failed") : t("upload.uploading", "Uploading…")}
                </span>
              </span>
              <button type="button" onClick={() => cancel(u_4.id)} aria-label={u_4.status === "error" ? t("upload.dismiss", "Dismiss") : t("buttons.cancel", "Cancel")} title={u_4.status === "error" ? t("upload.dismiss", "Dismiss") : t("buttons.cancel", "Cancel")} className={ICON_BUTTON}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>)}
          {value.map(file_2 => <li key={file_2.id} className="flex items-center gap-3 px-4 py-3">
              <FileIcon type={file_2.type} />
              <a href={file_2.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-[15px] text-[var(--foreground)] hover:text-[#0066CC] dark:hover:text-[#2997FF]">
                {file_2.name}
              </a>
              <button type="button" onClick={() => remove(file_2)} disabled={removing === file_2.id} aria-label={t("upload.remove", "Remove")} title={t("upload.remove", "Remove")} className={`${ICON_BUTTON} hover:!text-[#E30000] hover:!bg-[#E30000]/10`}>
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              </button>
            </li>)}
        </ul>}

      {shownError && <p role="alert" className="mt-2 text-[12px] text-[#E30000]">
          {shownError}
        </p>}
    </div>;
}